<template>
  <div
    ref="phoneField"
    :class="['phone-field', { 'phone-field--round': roundBorder }]"
  >
    <input
      ref="phoneInput"
      :value="valueWithoutCode"
      type="tel"
      @input="updateValue"
      @keyup="$emit('clearSubmitError', 'phone')"
      @keydown.space.prevent
    >

    <div
      v-if="curError && checkValidate"
      class="phone-field__error"
    >
      {{ curError || (checkValidate ? 'введите телефон*' : '') }}
    </div>
  </div>
</template>

<script>
// https://github.com/jackocnr/intl-tel-input/blob/79e81d1deb20da74cbfb220415138bf04b9c95f0/README.md
import intlTelInput from 'intl-tel-input';

export default {
  name: 'FormPhoneField',
  props: {
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorsList: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Телефон',
    },
    roundBorder: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    instance: null,
    isCurrentlyValid: false,
    checkValidate: false,
    curError: null,
  }),

  computed: {
    withoutMask() {
      return this.$route.name === 'online-school-ceny-dubai';
    },
    valueWithoutCode() {
      if (!this.value) return '';
      if (!this.instance) return this.value;

      const country = this.instance?.getSelectedCountryData() || { dialCode: '' };
      const regex = new RegExp(`^\\+${country.dialCode}`);

      return this.value.replace(regex, '');
    },
  },

  watch: {
    disabled(newValue) {
      if (this.instance) {
        this.instance.setDisabled(newValue);
      }
    },
    value(newValue) {
      if (this.instance && newValue !== this.instance.getNumber()) {
        this.instance.setNumber(newValue);
      }
    },
    errorsList() {
      if (this.errorsList.some((elem) => elem.objectField === 'phone')) {
        this.isCurrentlyValid = false;
        this.curError = (this.errorsList.find((elem) => elem.objectField === 'phone').message);
      }
    },
  },

  mounted() {
    if (!this.withoutMask) {
      window.intlTelInput = intlTelInput;

      // ждем появления $refs.input
      setTimeout(this.initIntTelInput, 300);
    }
  },
  beforeDestroy() {
    if (this.instance) {
      this.instance.destroy();
      if (this.$refs.input) {
        this.$refs.input.removeEventListener('countrychange', this.updateCountry);
      }
    }
  },

  methods: {
    initIntTelInput() {
      this.instance = intlTelInput(this.$refs.phoneInput, {
        autoPlaceholder: 'off',
        containerClass: 'phone-field-input',
        formatAsYouType: true,
        initialCountry: 'ru',
        onlyCountries: ['ru', 'by', 'kz'],
        placeholder: this.placeholder,

        showSelectedDialCode: true,
        strictMode: true,
        utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@20.3.0/build/js/utils.js',
        i18n: {
          selectedCountryAriaLabel: 'Выбранная страна',
          noCountrySelected: 'Страна не выбрана',
          countryListAriaLabel: 'Список стран',
          searchPlaceholder: 'Поиск',
          zeroSearchResults: 'Нет результата',
        },
      });

      if (this.value) {
        this.instance.setNumber(this.value);
      }

      if (this.disabled) {
        this.instance.setDisabled(true);
      }

      this.$refs.phoneInput.addEventListener('countrychange', this.updateCountry);

      const computedStyles = window.getComputedStyle(this.$refs.phoneInput);
      const fontSize = computedStyles.getPropertyValue('font-size');

      this.$refs.phoneField.querySelector('.iti__selected-dial-code').style.fontSize = fontSize;
      this.$refs.phoneField.querySelector('.iti__country-list .iti__country').style.fontSize = fontSize;
    },

    updateValidity() {
      if (!this.instance) return;

      this.isCurrentlyValid = this.instance.isValidNumberPrecise();
      // const isCurrentlyValid = this.options.strictMode
      //   ? this.instance.isValidNumberPrecise()
      //   : this.instance.isValidNumber();

      this.curError = this.isCurrentlyValid ? null : 'Не правильный номер';

      // this.$emit(
      //   'changeErrorCode',
      //   isCurrentlyValid ? null : this.instance.getValidationError(),
      // );
      // this.instance.getValidationError();
      // const validationError = {
      //   "IS_POSSIBLE": 0,
      //   "INVALID_COUNTRY_CODE": 1,
      //   "TOO_SHORT": 2,
      //   "TOO_LONG": 3,
      //   "IS_POSSIBLE_LOCAL_ONLY": 4,
      //   "INVALID_LENGTH": 5,
      // };
    },

    // для вызова из родительского компонента
    validate() {
      this.checkValidate = true;
      this.updateValidity();
      return this.isCurrentlyValid;
    },

    // eslint-disable-next-line no-unused-vars
    updateValue(e) {
      const number = this.instance?.getNumber() || e?.target?.value || '';
      this.$emit('input', number);
      this.updateValidity();
    },

    updateCountry() {
      this.updateValue();
      this.updateValidity();
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'intl-tel-input/build/css/intlTelInput.css';

.phone-field {
  width: 100%;
  position: relative;

  &:has(.phone-field__error) {
    input {
      border-color: #ff0040 !important;
    }
  }

  &__error {
    position: absolute;
    top: -7px;
    right: 20px;

    padding: 0 5px;

    background: $white-color;

    font-size: 14px;
    line-height: 14px;
    color: #ff0040;

    opacity: 1;
    transition: 0.2s ease-out;
  }

  &--round input {
    border-radius: 50px;
  }

  input {
    font-size: inherit;
  }
}

</style>

<style lang="scss">

.iti,
.iti__flag-container {
  font-size: inherit;
}

.phone-field-input {
  width: 100%;

  .iti__selected-flag {
    background-color: transparent;
    padding-left: 20px;
    border-radius: 15px 0 0 15px;
    font-size: inherit;
  }

  .iti__dropdown-content {
    color: black;
    font-size: inherit;

    input.iti__search-input {
      color: black !important;
      height: auto !important;
      border: none !important;
      background-color: transparent !important;
      font-size: inherit;
    }
  }

  .iti__country-list .iti__country {
    font-size: inherit;
  }

  .iti__selected-dial-code {
    color: inherit;
    font-size: inherit;
  }

  .iti__arrow {
    display: none;
  }

  &.phone-field--round {
    .iti__selected-flag {
      border-radius: 50px 0 0 50px;
    }
  }
}

</style>
